import * as React from 'react';

import { PlanType } from '@showbie-socrative/socrative-utils';
import { translate } from '~l10n/client';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';

import { UIButton } from '~shared/components/buttons';
import { DialogConfirmation } from '~shared/components/Dialog';
import { selectedTeamIdAtom } from '~teacher/components/Account/atoms';
import { useMaxSeatCountForSelectedTeamSubscription } from '~teacher/components/Account/hooks/useMaxSeatCount';
import { useSeatsUsedForSelectedTeam } from '~teacher/components/Account/TeamMembers/InviteTeamMembersDialog';
import { useTeamSubscriptionPlan } from '~teacher/components/Account/TeamMembers/TeamMembersTable';
import { ContactSalesLinkButton } from '~teacher/components/Account/TeamMembers/TeamMembersTable/AddSeatsDialog/Components/ContactSalesLinkButton';

import platform from '../../../../../../../../../shared/Platform';
import { ManageSeatsButton } from './Components/ManageSeatsButton';

const isDialogOpen = atom<boolean>(false);

export function InviteOverPlanSeatLimitDialog() {
  const selectedTeamId = useAtomValue(selectedTeamIdAtom);
  const [isOpen, setIsOpen] = useAtom(isDialogOpen);
  const selectedTeam = useAtomValue(selectedTeamIdAtom);

  const maxSeatsQuery = useMaxSeatCountForSelectedTeamSubscription();
  const teamSubscriptionPlan = useTeamSubscriptionPlan(selectedTeamId);

  const maxSeatCount = maxSeatsQuery.data?.maxSeats ?? 1;

  return (
    <DialogConfirmation
      id="exceeded-total-plan-seats-dialog"
      open={!platform.isWrapperApp() && isOpen}
      title={translate('Your Plan Has a {0} Seat Limit').format(maxSeatCount)}
      onClose={() => setIsOpen(false)}
      actions={[
        <UIButton key="done" onClick={() => setIsOpen(false)}>
          {translate('Done')}
        </UIButton>,
        ...(teamSubscriptionPlan.data === PlanType.Organization
          ? [
              <ContactSalesLinkButton
                key="contact-sales"
                onClick={() => {
                  setIsOpen(false);
                }}
              />,
            ]
          : [
              <ManageSeatsButton
                isLoading={false}
                selectedTeamId={selectedTeam}
                key="manage-seats"
              >
                {translate('Upgrade')}
              </ManageSeatsButton>,
            ]),
      ]}
    >
      {translate('Upgrade to Socrative Pro to purchase more seats.')}
    </DialogConfirmation>
  );
}

InviteOverPlanSeatLimitDialog.useOpenDialog = () => {
  const setIsDialogOpen = useSetAtom(isDialogOpen);

  return () => {
    setIsDialogOpen(true);
  };
};

export function getDidInviteOverPlanSeatLimit({
  maxSeats,
  emailCount,
  seatsUsed,
}: {
  maxSeats: number;
  emailCount: number;
  seatsUsed: number;
}) {
  // If max seat count is null it means that there is no limit and it's not possible to be over it.
  if (maxSeats === null || maxSeats === undefined || !isFinite(maxSeats)) {
    return false;
  }

  return seatsUsed + emailCount > maxSeats;
}

export function useDidInviteOverPlanSeatLimit() {
  const planMaxSeatCountQuery = useMaxSeatCountForSelectedTeamSubscription();
  const seatsUsedQuery = useSeatsUsedForSelectedTeam();

  const maxSeats = planMaxSeatCountQuery?.data?.maxSeats;
  const seatsUsed = seatsUsedQuery.data;

  const didInviteOverPlanSeatLimit = (emailCount: number) =>
    getDidInviteOverPlanSeatLimit({
      seatsUsed,
      maxSeats,
      emailCount,
    });

  return {
    planMaxSeatCountQuery,
    seatsUsedQuery,
    didInviteOverPlanSeatLimit,
  };
}
